import type { ComponentProps } from 'react';
import { Icon } from '@/components/primitives/icon';
import {
  isTinted,
  TONE_ALERT_ICON,
  TONE_CLASSES,
  type Tone,
} from '@/lib/status';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'info' | 'warning' | 'destructive' | 'success';

const TONE: Record<Variant, Tone> = {
  default: 'neutral',
  info: 'info',
  warning: 'warning',
  destructive: 'danger',
  success: 'success',
};

/**
 * An inline message. Tone, icon and words together; the tint only appears for
 * info, warning and danger.
 */
export function Alert({
  variant = 'default',
  className,
  children,
}: ComponentProps<'div'> & { variant?: Variant }) {
  const tone = TONE[variant];
  const classes = TONE_CLASSES[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      data-slot="alert"
      className={cn(
        'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-sm',
        isTinted(tone)
          ? [classes.tint, classes.border, classes.ink]
          : 'border-border bg-card text-foreground',
        className,
      )}
    >
      <Icon
        icon={TONE_ALERT_ICON[tone]}
        size={16}
        className={cn('mt-0.5', classes.ink)}
      />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
